// Align a sheet under the model from two point pairs (owner, 2026-09-18:
// "2 grid intersections per level on the sheet + the same 2 in the model").
//
// The tracking page lays the sheet as a plane in the model's ground plane
// (ModelStage): width W model units, height W / aspect, turned θ about the
// vertical, centred at P. A sheet point in percent (x%, y% from the top
// left) sits at
//     u = (x/100 - 0.5) W,   v = (0.5 - y/100) W / aspect
//     X = Px + u cos θ - v sin θ,   Z = Pz - (u sin θ + v cos θ)
// Written with complex numbers  w = X - iZ,  q = (x/100 - 0.5) + i (0.5 - y/100) / aspect:
//     w = A + C q,   A = Px - i Pz,   C = W e^{iθ}
// so two pairs fix C = (w1 - w2) / (q1 - q2) and A = w1 - C q1 exactly:
// scale, rotation and position, nothing guessed. A third pair, if given,
// only reports how far off it lands (the check).
const q = (p, aspect) => ({ re: p.x / 100 - 0.5, im: (0.5 - p.y / 100) / aspect });
const w = (m) => ({ re: m.x, im: -m.z });
const sub = (a, b) => ({ re: a.re - b.re, im: a.im - b.im });
const mul = (a, b) => ({ re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re });
const div = (a, b) => { const d = b.re * b.re + b.im * b.im; return { re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d }; };

// A sheet and its model are never mirror images of each other. Two pairs
// cannot tell (they always fit a turn), but a third can: fit the MIRRORED
// sheet (q conjugated) through A and B and see where C lands. When C is off
// as placed (over 3 units) yet lands at least four times closer mirrored, the
// pairs were picked mirror-wise - A and B swapped on one side, or a point
// taken off the wrong grid line - and the placement must not be used.
const conj = (a) => ({ re: a.re, im: -a.im });
function mirrorCheck(sheetPts, modelPts, aspect) {
  const q1 = conj(q(sheetPts[0], aspect)); const q2 = conj(q(sheetPts[1], aspect)); const q3 = conj(q(sheetPts[2], aspect));
  const dq = sub(q1, q2);
  if (Math.hypot(dq.re, dq.im) < 1e-9) return null;
  const C = div(sub(w(modelPts[0]), w(modelPts[1])), dq);
  const A = sub(w(modelPts[0]), mul(C, q1));
  const pred = { re: A.re + mul(C, q3).re, im: A.im + mul(C, q3).im };
  return Math.hypot(pred.re - w(modelPts[2]).re, pred.im - w(modelPts[2]).im);
}
const isMirrored = (residual, mirrorResidual) => residual != null && mirrorResidual != null && residual > 3 && mirrorResidual * 4 < residual;

/**
 * sheetPts: [{x, y}] percent of the sheet image; modelPts: [{x, z}] world plan points
 * aspect: sheet image width / height
 * -> { scale, rotation (degrees), position: {x, z}, residual (model units, from a 3rd pair),
 *      mirrorResidual, mirrored (true: the pairs describe a mirror image - do not use) } or null
 */
export function alignFromPoints(sheetPts, modelPts, aspect) {
  if (!sheetPts || !modelPts || sheetPts.length < 2 || modelPts.length < 2 || !(aspect > 0)) return null;
  const q1 = q(sheetPts[0], aspect); const q2 = q(sheetPts[1], aspect);
  const dq = sub(q1, q2);
  if (Math.hypot(dq.re, dq.im) < 1e-9) return null;
  const C = div(sub(w(modelPts[0]), w(modelPts[1])), dq);
  const A = sub(w(modelPts[0]), mul(C, q1));
  const scale = Math.hypot(C.re, C.im);
  const rotation = (Math.atan2(C.im, C.re) * 180) / Math.PI;
  let residual = null; let mirrorResidual = null;
  if (sheetPts[2] && modelPts[2]) {
    const pred = { re: A.re + mul(C, q(sheetPts[2], aspect)).re, im: A.im + mul(C, q(sheetPts[2], aspect)).im };
    residual = Math.hypot(pred.re - w(modelPts[2]).re, pred.im - w(modelPts[2]).im);
    mirrorResidual = mirrorCheck(sheetPts, modelPts, aspect);
  }
  return { scale, rotation, position: { x: A.re, z: -A.im }, residual, mirrorResidual, mirrored: isMirrored(residual, mirrorResidual) };
}

// The other way: where a world plan point lies on the sheet (percent).
export function sheetPercentOf(point, sheet, aspect) {
  const W = Number(sheet.scale) || 100; const th = ((Number(sheet.rotation) || 0) * Math.PI) / 180;
  const C = { re: W * Math.cos(th), im: W * Math.sin(th) };
  const A = { re: Number(sheet.position?.x) || 0, im: -(Number(sheet.position?.z) || 0) };
  const qq = div(sub(w(point), A), C);
  return { x: (qq.re + 0.5) * 100, y: (0.5 - qq.im * aspect) * 100 };
}

// "Save on all levels" (owner, 2026-10-13): the body one page's alignment
// becomes on ANOTHER page - same drawing, scale, turn and plan position, same
// model move; the page keeps its own sheet height (the floor it is drawn at)
// and opacity. Levels and camera are not in the body, so they stay too.
export function alignmentForPage(body, page) {
  const own = (page && page.sheet) || {};
  const src = (body && body.sheet) || {};
  return {
    sheetId: body.sheetId,
    page: body.page,
    sheet: {
      ...src,
      opacity: own.opacity != null ? own.opacity : src.opacity,
      position: { ...(src.position || {}), y: Number(own.position && own.position.y) || 0 },
    },
    model: body.model,
  };
}
